import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import CheckBox from 'components/CheckBox'
import AdminNavbar from 'components/Navbars/AdminNavbar'
import Select from 'components/Inputs/Select'
import MoneyInput from 'components/Inputs/MoneyInput'
import NumberInput from 'components/Inputs/NumberInput'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import serviceValidator from 'validators/Admin/serviceValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Container,
  Form,
  FormField,
  Title,
  Input,
  Row,
  InfoText,
  Label,
  WarningText,
  ErrorContainer
} from './styles'

export default function NewService () {
  const [errorMessage, setErrorMessage] = useState (null)
  const [categories, setCategories] = useState ([])
  const [loading, setLoading] = useState (true)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const token = AuthService.getToken ()
  const navigate = useNavigate ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  const getData = async () => {
    try {
      const { data: categories } = await cutileiApi.get ('/service_categories', requestConfig)

      setCategories (categories.map (category => ({
        label: category.name,
        value: category.id
      })))
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  return (
    <Container>
      <AdminNavbar/>
      {loading ? <Loading/> : (
        <Formik
          validationSchema={serviceValidator}
          initialValues={{
            'number': '',
            'name': '',
            'description': '',
            'category': null,
            'price': 0,
            'cutileiService': false,
          }}
          onSubmit={async (values, { setSubmitting }) => {
            const { category, ...service } = values

            try {
              await cutileiApi.post ('/services', {
                serviceCategoryId: category.value,
                ...service
              }, requestConfig)

              setSubmitting (false)
              toggleOkDialog ()
            } catch (error) {
              setSubmitting (false)
              logApiError (error)
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit
          }) => (
            <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
              <Title>Cadastrar serviço</Title>
              <Row>
                <FormField>
                  <Input 
                    placeholder='Nome'
                    value={values.name}
                    onChange={handleChange ('name')} 
                    onBlur={handleBlur ('name')}
                  />
                  {touched.name && errors.name && (
                    <WarningText>
                      {errors.name}
                    </WarningText>
                  )}
                </FormField>
                <FormField>
                  <Select
                    name='category'
                    placeholder='Categoria...'
                    value={values.category}
                    options={categories}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    error={errors.category}
                    touched={touched.category}
                  />
                  {touched.category && errors.category && (
                    <WarningText>
                      {errors.category}
                    </WarningText>
                  )}
                </FormField>
              </Row>
              <Row>
                <FormField>
                  <MoneyInput
                    name='price'
                    placeholder='Preço (R$)'
                    value={values.price}
                    onValueChange={setFieldValue}
                    onBlur={handleBlur ('price')}
                  />
                  {touched.price && errors.price && (
                    <WarningText>
                      {errors.price}
                    </WarningText>
                  )}
                </FormField>
                <FormField>
                  <NumberInput
                    name='number'
                    placeholder='Número na lista (opcional)'
                    value={values.number}
                    onChange={setFieldValue}
                    onBlur={handleBlur ('number')}
                  />
                  {touched.number && errors.number && (
                    <WarningText>
                      {errors.number}
                    </WarningText>
                  )}
                </FormField>
              </Row>
              <Row>
                <FormField>
                  <Input 
                    placeholder='Descrição do serviço'
                    value={values.description}
                    onChange={handleChange ('description')} 
                    onBlur={handleBlur ('description')}
                  />
                  {touched.description && errors.description && (
                    <WarningText>
                      {errors.description}
                    </WarningText>
                  )}
                </FormField>
              </Row>
              <Row>
                <CheckBox
                  id='cutileiService'
                  style={{marginRight: 6, marginTop: 3}}
                  value={values.cutileiService}
                  onValueChange={value => setFieldValue ('cutileiService', value)}
                />
                <Label htmlFor='cutileiService'>
                  Serviço Cutilei
                </Label>
              </Row>
              {errorMessage && (
                <ErrorContainer>
                  <InfoText>{errorMessage}</InfoText>
                </ErrorContainer>
              )}
              <AlertDialog
                visible={showOkDialog}
                title='Sucesso'
                message='Serviço cadastrado!'
                confirmText='Ok'
                onConfirm={() => navigate ('/admin/config', { replace: true })}
                onClose={() => navigate ('/admin/config', { replace: true })}
              />
              <MainButton
                label='Criar serviço'
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                width={180}
              />
            </Form>
          )}
        </Formik>
      )}
    </Container>
  )
}
